import { useState, useMemo } from "react";

const DEFAULT_PAGE_SIZE = 20;

export function usePagination<T>(items: T[], initialPageSize = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset to page 0 when items change length significantly (e.g. filter change)
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);

  const paged = useMemo(
    () => items.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [items, safePage, pageSize],
  );

  return {
    paged,
    page: safePage,
    totalPages,
    totalItems: items.length,
    setPage,
    hasNext: safePage < totalPages - 1,
    hasPrev: safePage > 0,
    next: () => setPage((p) => Math.min(p + 1, totalPages - 1)),
    prev: () => setPage((p) => Math.max(p - 1, 0)),
    reset: () => setPage(0),
    pageStart: safePage * pageSize + 1,
    pageEnd: Math.min((safePage + 1) * pageSize, items.length),
    pageSize,
    setPageSize,
  };
}
